<template>
  <div class="result" :class="{'is-loading': loading.cities || loading.trips || loading.sorting, 'show-title': showTitle}">
    <title-result
      class="is-hidden-desktop result__title-result"
      :departure="departure"
      :arrived="arrived"
      :when="getDate(query.when)"
      :showTicket="showTitle"
      @showTicket="setShowTicket"
    />
    <Title
      :departure="departure"
      :arrived="arrived"
      :price="price"
      :when="getDate(query.when)"
      class="result__title is-hidden-touch"
    />
    <Search
      ref="search-form"
      addinional-class="is-hidden-touch"
      :initial-cities="cities"
      :async="false"
      :initialFrom="getCityFrom()"
      :initialTo="getCityTo()"
      :initialWhen="getDate(query.when)"
      :initialAdult="getNumber(query.adult)"
      :initialChildren="getNumber(query.children)"
      :initialBackDate="getDate(query.whenback, true)"
      :initialIsBack="getNumber(query.goback)"
      :initialChange="getChange(query.change)"
      @search:new="getTrips"
      v-if="!loading.cities"
      class="result__search"
    />
    <section class="octobus__section tickets result__tickets" v-if="trips.length !== 0">
      <div
        :class="{ 'octobus__container': !isFullWidth }"
        class="is-hidden-mobile result__tickets-title"
      >
        <div
          class="octobus__columns trips-menu has-text-weight-light is-uppercase is-size-6-tablet"
          @click.prevent="setSorting">
          <div class="octobus__column" data-sort="dtDepSec">
            <span data-sort="dtDepSec">{{$t('result.departure')}}</span>
            <font-awesome-icon
              :icon="getArrow('dtDepSec')"
              data-sort="dtDepSec"
              :class="{'is-active': isActiveSort('dtDepSec')}"
            />
          </div>
          <div class="octobus__column" data-sort="wayTimeSec">
            <span data-sort="wayTimeSec">{{$t('result.inway')}}</span>
            <font-awesome-icon
              :icon="getArrow('wayTimeSec')"
              data-sort="wayTimeSec"
              :class="{'is-active': isActiveSort('wayTimeSec')}"
            />
          </div>
          <div class="octobus__column" data-sort="dtArrSec">
            <span data-sort="dtArrSec">{{$t('result.arrived')}}</span>
            <font-awesome-icon
              :icon="getArrow('dtArrSec')"
              data-sort="dtArrSec"
              :class="{'is-active': isActiveSort('dtArrSec')}"
            />
          </div>
          <div class="octobus__column" data-sort="places">
            <span data-sort="places">{{$t('result.freeplace')}}</span>
            <font-awesome-icon
              :icon="getArrow('places')"
              data-sort="places"
              :class="{'is-active': isActiveSort('places')}"
            />
          </div>
          <div class="octobus__column" data-sort="fullPrice">
            <span data-sort="fullPrice">{{$t('result.price')}}</span>
            <font-awesome-icon
              :icon="getArrow('fullPrice')"
              data-sort="fullPrice"
              :class="{'is-active': isActiveSort('fullPrice')}"
            />
          </div>
        </div>
      </div>
      <Ticket :trip="trip" v-for="trip in trips" :key="trip.tripId" class="result__tickets-item" />
    </section>
    <section class="octobus__section" v-if="isNoData && !query.whenback">
      <div :class="{ 'octobus__container': !isFullWidth }">
        <div class="octobus__columns">
          <div class="octobus__column octobus__content has-text-centered">
            <h3>{{$t('result.notfound.title')}}</h3>
            <p>{{$t('result.notfound.body')}}</p>
          </div>
        </div>
      </div>
    </section>
    <section class="octobus__section" v-if="isNoData && query.whenback">
      <div :class="{ 'octobus__container': !isFullWidth }">
        <div class="octobus__columns">
          <div class="octobus__column octobus__content has-text-centered">
            <h3>{{$t('result.notfound.title')}}</h3>
            <p>{{$t('result.notfound.body')}}</p>
          </div>
        </div>
      </div>
    </section>
    <InfiniteLoading
      v-if="!isNoData"
      @infinite="infiniteHandler"
      spinner="waveDots"
      ref="infiniteLoading"
      :distance="10"
    >
      <span slot="no-results">
      </span>
      <span slot="no-more">
        <div v-if="trips > 10">
          {{$t('result.notfound.nomore')}}
        </div>
      </span>
      <span slot="spinner" v-if="trips.length === 0" />
    </InfiniteLoading>
  </div>
</template>

<script>
import moment from 'moment';
import minBy from 'lodash/minBy';
import isObject from 'lodash/isObject';
import orderBy from 'lodash/orderBy';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import { faSortDown, faSortUp } from '@fortawesome/fontawesome-free-solid';
import api from '@/api';
import InfiniteLoading from 'vue-infinite-loading';
import Search from './Search';
import Ticket from './Ticket/Ticket';
import Title from './_common/Title';
import TitleResult from './_common/TitleResult';

export default {
  name: 'Result',
  components: {
    Search,
    Ticket,
    InfiniteLoading,
    Title,
    FontAwesomeIcon,
    TitleResult,
  },
  data() {
    return {
      query: this.$route.query,
      locale: this.$framelocale,
      cities: [],
      trips: [],
      scrollData: [],
      error: {
        code: '',
      },
      sorting: {
        field: this.$sorting.field,
        type: this.$sorting.type,
      },
      loading: {
        cities: true,
        trips: true,
        sorting: false,
      },
      showTitle: false,
    };
  },
  /* eslint-disable object-shorthand */
  watch: {
    '$route.fullPath'() {
      this.query = this.$route.query;
      if (this.$search) {
        this.getTrips(this.query);
      } else {
        this.getTrips(true);
      }
    },
  },
  filters: {
    date(value) {
      return moment(value).format('DD MMMM');
    },
  },
  methods: {
    getLink(newDate, backDate) {
      return this.getUrl(newDate, backDate).replace('passcount', 'adult');
    },
    getPriceMin() {
      return (minBy(this.scrollData, 'fullPrice') || {}).fullPrice;
    },
    getCityName(id) {
      return (this.cities.filter(city => Number(city.id) === Number(id))[0] || {}).name;
    },
    getCity(id) {
      return this.cities.filter(city => Number(city.id) === Number(id))[0];
    },
    getDate(date, isBack) {
      if (this.$search) {
        return this.$search.when;
      }
      if (isBack) {
        return date ? new Date(date) : new Date(moment(this.query.when).add(1, 'd'));
      }
      return date ? new Date(date) : new Date();
    },
    getNumber(data) {
      return Number(data) || 0;
    },
    getChange(change) {
      return Number(change);
    },
    setShowTicket(value) {
      this.showTitle = value;
    },
    isActiveSort(field) {
      if (field === this.sorting.field) {
        return true;
      }

      return false;
    },
    setSorting(event) {
      const { sort } = event.target.dataset;

      if (sort === this.sorting.field) {
        this.sorting.type = this.sorting.type === 'asc' ? 'desc' : 'asc';
      } else {
        this.sorting.field = sort;
        this.sorting.type = 'asc';
      }

      this.scrollData = orderBy(this.scrollData, obj => parseFloat(obj[this.sorting.field]), this.sorting.type);
      this.trips = [];
      this.loading.sorting = true;
      this.reloadScroll();
    },
    getCityFrom() {
      const from = (this.$search && this.$search.from) || this.query.from;

      return this.getCity(from);
    },
    getCityTo() {
      const to = (this.$search && this.$search.to) || this.query.to;

      return this.getCity(to);
    },
    infiniteHandler($state) {
      setTimeout(() => {
        if (this.loading.trips) {
          $state.loaded();
          return true;
        }

        const temp = [];
        for (let i = this.trips.length; i <= this.trips.length + 5; i += 1) {
          const trip = this.scrollData[i];

          if (isObject(trip)) {
            temp.push(trip);
          }
        }
        this.trips = this.trips.concat(temp);
        if (this.trips.length === this.scrollData.length) {
          $state.complete();
        } else {
          $state.loaded();
        }
        this.loading.sorting = false;
        return true;
      }, 200);
    },
    /* eslint no-param-reassign: "off" */
    getUrl(newDate = this.query.when, backDate, reloadNewData) {
      const { goback, whenback } = this.query;
      let { from, to, adult, change } = this.query;

      if (this.$search) {
        from = this.$search.from;
        to = this.$search.to;
        newDate = newDate && (newDate !== this.query.when) ? newDate : this.$search.when;

        if (this.$searchChanges) {
          change = this.$searchChanges;
        }
      }

      if (this.$search && reloadNewData) {
        from = reloadNewData.from;
        to = reloadNewData.to;
        newDate = reloadNewData.when;
        adult = reloadNewData.adult;
        change = reloadNewData.change;
      }

      const whenParse = moment(newDate).format('YYYY-MM-DD');
      const passcount = Number(adult) || 1;
      let searchParams = `?from=${from}&to=${to}&when=${whenParse}&passcount=${passcount}`;
      let backdate = null;

      if (Number(change)) {
        searchParams += '&change=1';
      } else {
        searchParams += '&change=0';
      }

      if (this.$search && reloadNewData && backDate) {
        searchParams += reloadNewData.backdate;
      }

      if (goback && whenback) {
        backdate = backDate || moment(whenback).format('YYYY-MM-DD');
        searchParams += `&goback=1&whenback=${backdate}`;
      }

      return searchParams;
    },
    getTrips(reload) {
      let searchParams = null;

      searchParams = this.getUrl();

      if (this.$search && reload) {
        const isBack = reload.backdate || '';

        searchParams = this.getUrl('', isBack, reload);
      }

      this.loading.trips = true;
      this.error = {};
      api.Trip.search(searchParams).then((res) => {
        const { error } = res;

        this.loading.trips = false;
        if (error) {
          this.error = error;
          this.scrollData = [];
        } else {
          this.scrollData = orderBy(res, obj => parseFloat(obj[this.sorting.field]), this.sorting.type);
        }

        if (reload) {
          this.trips = [];
          this.reloadScroll();
        }
      });
    },
    reloadScroll() {
      this.$nextTick(() => {
        this.$refs.infiniteLoading.$emit('$InfiniteLoading:reset');
      });
    },
    getArrow(field) {
      if (field === this.sorting.field && this.sorting.type === 'desc') {
        return faSortUp;
      }

      return faSortDown;
    },
  },
  computed: {
    departure() {
      return this.getCityName(this.query.from) || '';
    },
    arrived() {
      return this.getCityName(this.query.to) || '';
    },
    price() {
      return this.getPriceMin() || '';
    },
    isNoData() {
      return this.error.code === 'E_NODATA';
    },
    isFullWidth() {
      return this.$fullwidth;
    },
  },
  created() {
    api.Content.cities().then((res) => {
      const { error } = res;

      this.loading.cities = false;
      if (error) {
        this.error = error;
      } else {
        this.cities = res;
      }
    });

    this.getTrips();
  },
};
</script>

<style lang="scss" scoped>

#app, #auth {
  .result {
    &.is-loading {
      height: 100vh;
    }

    .tickets {
      min-height: 60vh;
      padding: 3rem 1.5rem;
    }

    .trips-menu {
      margin-bottom: 1rem;
      margin-left: 1.25rem;

      .octobus__column {
        cursor: pointer;

        .fa-sort-down {
          vertical-align: top;
        }

        .fa-sort-up {
          vertical-align: bottom;
        }

        .fa-sort-up,
        .fa-sort-down {
          color: #808080;

          &.is-active{
            color: $color-main;
          }
        }
      }
    }
  }
}

/* Small Devices, Tablets */
@media only screen and (max-width : 1023px) {
  #app, #auth {
    .result {

      .tickets {
        padding: 0rem 1rem;
      }
    }
  }
}

@media only screen and (max-width: 768px) {
  #app, #auth {
    .result:not(.show-title) {
      .result__title-result {
        display: block;
      }

      .result__search {
        display: none;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Result.vue